import 'quill/dist/quill.snow.css';

import Quill from 'quill';

import styled from '@emotion/styled';

import { useEffect, useState } from 'react';

import useCreateJobPost from '../../../hooks/jobs/useCreateJobPost';
import {
  CreateJobPostDataType,
  createJobPostInitialValues,
  CreateJobPostSchema,
} from '../../../models/jobs/createJobPost.model';
import ValidatingFormSubmitButton from '../../Button/FormSubmitButton';
import { CustomDatePicker } from '../../Form/CustomDatePicker';
import { CustomTwoValDropDown } from '../../Form/CustomTwoValDropDown';
import { FormComponent } from '../../Form/FormComponent';
import { TextInputField } from '../../Form/FormField';
import NumberOnlyInputField from '../../Form/NumberOnlyInputField';
import RichTextInputField from '../../Form/RichTextInputField';
import { ToggleCheckBox } from '../../Form/ToggleCheckBox';
import ModalContainer from '../ModalContainer';
import { useAppSelector } from '../../../redux/store';
import useEditJob from '../../../hooks/jobs/useEditJob';
import NoticeIcon from '../../Icons/NoticeIcon'

const salaryInterval: Record<string, string> = {
  'Per Hour': 'HOURLY',
  'Per Day': 'DAILY',
  'Per Week': 'WEEKLY',
  'Per Month': 'MONTHLY',
  'Per Year': 'YEARLY',
};

const PostJobModal = ({ jobId }: { jobId?: string }) => {
  const { loading, createJobPost } = useCreateJobPost();

  const { loading: editing, editJob } = useEditJob(jobId || '');

  const handleSubmit = (values: CreateJobPostDataType, actions: any) => {
    if (String(values?.jobEndDate) === '') {
      delete values['jobEndDate'];
    }

    const payload = {
      ...values,
      payInterval: salaryInterval[values.payInterval],
    };

    // If jobId exists, call editJob; otherwise, create a new job.
    if (jobId) {
      editJob(payload)
        .then(() => {
          actions.resetForm({ values: createJobPostInitialValues });
          // Reset the react-quill editor.
          const quill = document.querySelector(
            '.ql-editor'
          ) as HTMLDivElement & { __quill?: Quill };
          if (quill && quill.__quill) {
            quill.__quill.setText('');
          }
        })
        .catch((error) => {
          // Handle edit error if needed.
          console.error('Failed to edit job:', error);
        });
    } else {
      createJobPost(payload, () => {
        actions.resetForm({ values: createJobPostInitialValues });
        // Reset the react-quill editor.
        const quill = document.querySelector('.ql-editor') as HTMLDivElement & {
          __quill?: Quill;
        };
        if (quill && quill.__quill) {
          quill.__quill.setText('');
        }
      });
    }
  };

  const jobsCreatedList = useAppSelector((state) => state.jobsCreatedList);

  const [formInitialValues, setFormInitialValues] = useState(
    createJobPostInitialValues
  );

  useEffect(() => {
    if (jobId) {
      const selectedJob = jobsCreatedList.jobData.find(
        (job) => job.id === jobId
      );
      if (selectedJob) {
        setFormInitialValues({
          ...createJobPostInitialValues,
          ...selectedJob,
          pay: String(selectedJob.pay),
          jobDescription:
            typeof selectedJob.jobDescription === 'object'
              ? selectedJob.jobDescription.jobDescription
              : selectedJob.jobDescription,
        });
      } else {
        setFormInitialValues(createJobPostInitialValues);
      }
    } else {
      setFormInitialValues(createJobPostInitialValues);
    }
  }, [jobId, jobsCreatedList]);

  return (
    <ModalContainer width="1080px">
      <FormComponent
        initialValues={formInitialValues}
        schema={CreateJobPostSchema}
        onSubmit={handleSubmit}
      >
        <Row className="two-even">
          <TextInputField
            label="Job Title"
            placeholder=""
            name="title"
            id="title"
            type="text"
          />
          <TextInputField
            label="Institution Name"
            placeholder=""
            name="institutionName"
            id="institutionName"
            type="text"
          />
        </Row>

        <Row className="three">
          <NumberOnlyInputField
            label="Years of Experience"
            placeholder=""
            name="yearsOfExperience"
            id="yearsOfExperience"
            type="text"
          />
          <CustomTwoValDropDown
            label="Salary"
            placeholder=""
            name="pay"
            id="pay"
            optionData={Object.keys(salaryInterval)}
            optionName="payInterval"
          />
          <TextInputField
            label="Job Location"
            placeholder=""
            name="location"
            id="location"
            type="text"
          />
        </Row>
        <RichTextInputField
          label="Job Description"
          placeholder=""
          name="jobDescription"
          id="jobDescription"
          type="text"
        />

        <Row className="four">
          <CustomDatePicker
            label="Start Date"
            placeholder=""
            name="jobStartDate"
            id="jobStartDate"
          />
          <CustomDatePicker
            label="Stop Date"
            placeholder=""
            name="jobEndDate"
            id="jobEndDate"
          />
          <CustomDatePicker
            label="Expiry Date"
            placeholder=""
            name="expiryDate"
            id="expiryDate"
          />
        </Row>

        <Row className="flex">
          <Row className="two">
            <ToggleCheckBox name="requiresCV">Requires a resume</ToggleCheckBox>
            <ToggleCheckBox name="isPublished">Publish</ToggleCheckBox>
            <Note>
              <NoticeIcon width={50} />
              If you would like to list this job now turn on this toggle, if you
              would lke to however publish it later you can do so using the edit
              option in your posted job.
            </Note>
          </Row>

          <ValidatingFormSubmitButton
            loading={jobId ? editing : loading}
            className="small h-fit-flex-end"
          >
            {jobId ? 'Save Changes' : 'Create'}
          </ValidatingFormSubmitButton>
        </Row>
      </FormComponent>
    </ModalContainer>
  );
};

export default PostJobModal;

const Row = styled.div`
  display: grid;
  width: 100%;
  gap: 16px;

  &.two-even {
    grid-template-columns: 1fr 1fr;
  }

  &.three {
    grid-template-columns: 1fr 2fr 2fr;
  }
  &.four {
    grid-template-columns: 1fr 1fr 1fr;
  }

  &.flex {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
  }

  @media (max-width: 884px) {
    &.two-even,
    &.three,
    &.four {
      grid-template-columns: 1fr;
      width: 100%;
    }
    &.flex {
      flex-direction: column;
      /* border: 1px solid red; */
      margin-left: auto;
      align-items: flex-end;
      gap: 42px;
    }
  }
`;

const Note = styled.div`
  font-weight: 400;
  font-size: 14px;
  width: 100%;
  max-width: 360px;
  display: flex;
  justify-content: start;
  align-items: start;
  gap: 1rem;
  color: ${({ theme }) => theme.palette.greyGrey2};
`;
